var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var ExpandPicklistConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { PicklistColumnMetadata } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonConverter, JsonDiscriminatorValue, JsonSchemaV2, MapConverter } from '@symon-ai/wisepipe-serialize';
import './picklistColumn'; // force import for JsonSchemaV2
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult, ToolValidationState } from './validation/toolValidationResult';
import { expandPicklistConfigSchemaValidation } from '../validations.generated';
export const expandPicklistConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:ExpandPicklistConfig',
    type: 'object',
    properties: {
        elementIDToExpand: { type: 'string', uid: 10 },
        inputMap: { type: 'object', additionalProperties: { type: 'string' } },
        picklistColumns: { type: 'array', items: { $ref: 'urn:wisepipe-common-pipe:PicklistColumn' }, minItems: 1 }
    },
    required: ['elementIDToExpand', 'inputMap', 'picklistColumns']
};
let ExpandPicklistConfig = ExpandPicklistConfig_1 = class ExpandPicklistConfig extends ToolConfig {
    constructor(elementIDToExpand, inputMap, picklistColumns, customFormats) {
        super(ToolType.EXPAND_PICKLIST, customFormats);
        this.elementIDToExpand = elementIDToExpand;
        this.inputMap = inputMap;
        this.picklistColumns = picklistColumns;
        JSON.validate(this, ExpandPicklistConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return ExpandPicklistConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return ExpandPicklistConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return ExpandPicklistConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return ExpandPicklistConfig_1.outs;
    }
    static get in() {
        return ExpandPicklistConfig_1.ins[0];
    }
    static get out() {
        return ExpandPicklistConfig_1.outs[0];
    }
    static get ins() {
        return ['multi'];
    }
    static get outs() {
        return ['default'];
    }
    // eslint-disable-next-line class-methods-use-this
    multiIns(inScope) {
        return [...inScope.names];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.EXPAND_PICKLIST.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.COMBINE], this.ins, this.outs, ['expand picklist', 'column', 'drill down'], true);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const exp = tsb.export(this.out);
        // needs at least 2 inputs
        if (ins.schemas.size >= 2) {
            // Copy all columns from the tool to expand
            const toolToExpandPort = this.inputMap.get(this.elementIDToExpand);
            const outputColumnNames = ins.schemas.at(toolToExpandPort).map((columnMetadata) => columnMetadata.column.name);
            exp.copyAll(toolToExpandPort);
            this.picklistColumns.forEach((picklistColumn) => {
                const hierarchy = picklistColumn.hierarchy.sort((a, b) => a.level - b.level); // sort hierarchy array by level
                let previousLevelSchema = ins.schemas.at(toolToExpandPort); // root level
                hierarchy.forEach((hierarchyNode) => {
                    // find from previous level the selected column with the picklist metadata
                    const picklistMetadata = previousLevelSchema?.find((columnMetadata) => columnMetadata.column.name === hierarchyNode.columnName)?.column.metadata;
                    // retrieve the referenced table from the picklist metadata and get the schema of the table from toolscope
                    const referencedTable = picklistMetadata.table;
                    const port = ins.incentivesDSMetadatas.getPortByDatasourceID(referencedTable);
                    const schema = ins.schemas.at(port);
                    // filter columns to include only the attributes specified in the hierarchy node
                    const attributeColumns = schema?.filter((columnMetadata) => hierarchyNode.attributes?.includes(columnMetadata.column.name)) ?? [];
                    // rename attribute columns if same name already exists in the output
                    const aliases = ToolConfig.computeAliases(outputColumnNames, attributeColumns.map((columnMetadata) => columnMetadata.column.name));
                    attributeColumns.forEach((columnMetadata) => {
                        const alias = aliases.get(columnMetadata.column.name);
                        exp.copy(port, columnMetadata, false, true, undefined, alias);
                        outputColumnNames.push(alias);
                    });
                    previousLevelSchema = schema;
                });
            });
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        // check that the tool has at least 2 inputs
        if (scope.schemas.size < 2 && this.inputMap.size < 2) {
            return ToolValidationResult.getPortMissingEdgesResult('One or more input nodes are missing.', 'The expand picklist tool requires at least two input nodes.');
        }
        // check that the tool to be expanded exists in incoming schema
        const toolToExpandPort = this.inputMap.get(this.elementIDToExpand);
        if (!toolToExpandPort) {
            return ToolValidationResult.getInvalidResult('Tool to expand not found in inputMap');
        }
        const toolToExpandSource = scope.schemas.at(toolToExpandPort);
        if (!toolToExpandSource) {
            return ToolValidationResult.getBadMetadataResult('Tool to expand not found in source');
        }
        // check that the tool to be expanded contains picklist columns
        const toolToExpandContainsPicklistCols = toolToExpandSource.some((columnMetadata) => columnMetadata.column.metadata instanceof PicklistColumnMetadata);
        if (!toolToExpandContainsPicklistCols) {
            return ToolValidationResult.getBadMetadataResult('Tool to expand does not contain picklist columns');
        }
        // for each picklist column, validate each level
        for (const picklistColumn of this.picklistColumns) {
            const hierarchy = picklistColumn.hierarchy.sort((a, b) => a.level - b.level); // sort hierarchy array by level
            let previousLevelSchema = scope.schemas.at(toolToExpandPort); // for root level, it is the schema of the tool to expand
            for (const hierarchyNode of hierarchy) {
                const colName = hierarchyNode.columnName;
                // check if the selected column exists in parent level and is a picklist column (aka has metadata)
                const parentColMetadata = previousLevelSchema?.find((columnMetadata) => columnMetadata.column.name === colName)
                    ?.column.metadata;
                if (!parentColMetadata) {
                    return new ToolValidationResult(ToolValidationState.MULTI_PORT_MAPPED_COLUMN_INVALID, `Column ${colName} not found in parent level`);
                }
                // check if referenced picklist table exists in source
                const picklistTable = parentColMetadata.table;
                const port = scope.incentivesDSMetadatas.getPortByDatasourceID(picklistTable);
                if (!port) {
                    return new ToolValidationResult(ToolValidationState.MULTI_PORT_MAPPED_INPUT_MISSING, `Port for picklist table ${picklistTable} not found in toolscope`);
                }
                const schema = scope.schemas.at(port);
                if (!schema) {
                    return new ToolValidationResult(ToolValidationState.MULTI_PORT_MAPPED_INPUT_MISSING, `Picklist table ${picklistTable} not found in source`);
                }
                // check if referenced picklist table contains exactly one Primary Key column. the primary key column is what actually used to expand the selected picklist column
                const primaryKeyColumns = scope.incentivesDSMetadatas.at(port)?.pkColumns;
                if (primaryKeyColumns?.length !== 1) {
                    return new ToolValidationResult(ToolValidationState.MULTI_PORT_MAPPED_COLUMN_INVALID, `Picklist table ${picklistTable} must contain exactly one primary key column`);
                }
                // check if all attributes exist in picklist table
                for (const attribute of hierarchyNode.attributes ?? []) {
                    if (!schema.some((columnMetadata) => columnMetadata.column.name === attribute)) {
                        return new ToolValidationResult(ToolValidationState.MULTI_PORT_MAPPED_COLUMN_INVALID, `Attribute ${attribute} not found in picklisted table ${picklistTable}`);
                    }
                }
                previousLevelSchema = schema;
            }
        }
        return ToolValidationResult.getValidResult();
    }
};
__decorate([
    JsonConverter(new MapConverter())
], ExpandPicklistConfig.prototype, "inputMap", void 0);
ExpandPicklistConfig = ExpandPicklistConfig_1 = __decorate([
    JsonSchemaV2(expandPicklistConfigSchema, expandPicklistConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.EXPAND_PICKLIST)
], ExpandPicklistConfig);
export { ExpandPicklistConfig };
//# sourceMappingURL=expandPicklistConfig.js.map